import { FC, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Button, Col, Popover, Row, Space, Tag, Typography } from 'antd';
import type { TooltipPlacement } from 'antd/es/tooltip';
import { HiOutlineMail } from 'react-icons/hi';
import { BsTelephone } from 'react-icons/bs';
import { CloseOutlined } from '@ant-design/icons';
import UserAvatar, { UserAvatarProps } from 'components/UserAvatar';
import TextCopyable from 'components/TextCopyable';
import styles from './index.module.less';

const { Text } = Typography;

interface UserPopoverContentProps {
  user?: any | undefined | null;
  showJobTitle?: boolean;
  onClose: () => void;
}

const UserPopoverContent: FC<UserPopoverContentProps> = ({ user, showJobTitle = true, onClose }) => {
  const history = useHistory();

  return (
    <Row
      style={{ position: 'relative', maxWidth: 320, padding: '8px 10px', columnGap: 12 }}
      wrap={false}
      onClick={(event) => event.stopPropagation()}
    >
      <Col flex="none" style={{ marginTop: 4 }}>
        <Space direction="vertical" align="center" size={4}>
          <UserAvatar user={user} size="xl" />

          {user?.uuid && (
            <Link
              to={`${history.location.pathname}?profile_uuid=${user.uuid}`}
              onClick={onClose}
              style={{ fontSize: 11 }}
            >
              Profile
            </Link>
          )}
        </Space>
      </Col>

      <Col flex="auto" style={{ fontSize: 12 }}>
        <div style={{ paddingRight: 26 }}>
          <Text style={{ fontSize: 14 }} strong ellipsis>
            {user?.name}
          </Text>
        </div>

        <div className="mb-m">{showJobTitle ? <Text type="secondary">{user?.job_title}</Text> : undefined}</div>

        {user?.phone ? (
          <div className="secondary" style={{ display: 'flex' }}>
            <BsTelephone style={{ fontSize: 14, marginRight: 4, flex: 'none' }} />
            <TextCopyable style={{ overflow: 'hidden' }}>{user?.phone}</TextCopyable>
          </div>
        ) : undefined}

        {user?.email ? (
          <div className="secondary" style={{ display: 'flex' }}>
            <HiOutlineMail style={{ fontSize: 16, marginRight: 4, flex: 'none' }} />
            <TextCopyable style={{ overflow: 'hidden' }}>{user?.email}</TextCopyable>
          </div>
        ) : undefined}
      </Col>

      <div style={{ position: 'absolute', top: 4, right: 4 }}>
        <Button type="link" size="middle" icon={<CloseOutlined className="secondary" />} onClick={onClose} />
      </div>
    </Row>
  );
};

interface UserAvatarPopoverProps extends UserAvatarProps {
  showName?: boolean;
  collapsible?: string;
  style?: React.CSSProperties;
  colored?: boolean;
  placement?: TooltipPlacement;
  hoverOnly?: boolean;
  showJobTitle?: boolean;
}

const UserAvatarPopover: FC<UserAvatarPopoverProps> = ({
  user,
  size,
  showName = false,
  collapsible = '',
  style = {},
  colored = false,
  placement = 'topLeft',
  hoverOnly = false,
  showJobTitle = true,
}) => {
  const [open, setOpen] = useState(false);
  const [trigger, setTrigger] = useState('hover');

  return (
    <Popover
      visible={open}
      trigger={hoverOnly ? 'hover' : trigger}
      placement={placement}
      overlayClassName={styles.overlayPopup}
      mouseEnterDelay={0.2}
      content={
        <UserPopoverContent
          user={user}
          showJobTitle={showJobTitle}
          onClose={() => {
            if (trigger !== 'hover') {
              setTrigger('hover');
            }
            setOpen(false);
          }}
        />
      }
      onVisibleChange={(visible) => {
        if (!visible && trigger !== 'hover') {
          setTrigger('hover');
        }
        setOpen(visible);
      }}
      destroyTooltipOnHide
    >
      <div
        className={collapsible}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexWrap: 'nowrap',
          overflow: 'hidden',
          ...style,
        }}
        onClick={
          hoverOnly
            ? undefined
            : (event) => {
                event.stopPropagation();
                event.preventDefault();
                setTrigger('click');
              }
        }
      >
        <UserAvatar user={user} size={size} style={{ cursor: 'pointer' }} />
        {showName && user ? (
          <Text ellipsis style={{ marginLeft: '4px', color: colored ? '#0044CC' : undefined }}>
            {user.name}
          </Text>
        ) : undefined}
      </div>
    </Popover>
  );
};

export default UserAvatarPopover;
